import React, { useRef, useEffect } from 'react';
import { NetworkStatus } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Spinner from '@atlaskit/spinner/spinner';
import { xcss, Box } from '@atlaskit/primitives';

import { useFeatureCodesInURL } from '@confluence/feature-codes';
import { ErrorDisplay } from '@confluence/error-boundary';
import { manuallyLogExpExposure } from '@confluence/feature-experiments';
import type { LoadingPriority } from '@confluence/loadable';
import {
	CREATE_PAGE_EXPERIENCE,
	ExperienceTrackerContext,
	VIEW_CONTENT_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
	VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
	VIEW_PAGE_SESSION_EXPERIENCE,
	LIVE_PAGE_VIEW_EXPERIENCE,
	useExperienceState,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import {
	FullPageEditorLoader,
	TreeRootForServerPlaceholderWrapper,
} from '@confluence/full-page-editor';
import type { NativeCollabInjectedProps } from '@confluence/native-collab';
import { NativeCollabLoader } from '@confluence/native-collab';
import { ContentViewedEvent } from '@confluence/content-body';
import { useLivePageStoreActions } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { useLivePageOnboardingNudgeEligible } from '@confluence/onboarding-editor/entry-points/hooks/useLivePageOnboardingNudgeEligible';
import { useLivePageInviteNudgeV2Eligible } from '@confluence/onboarding-editor/entry-points/hooks/useLivePageInviteNudgeV2Eligible';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { LivePageInviteNudge } from '@confluence/onboarding-editor/entry-points/components/LivePageInviteNudge';
import { fg } from '@confluence/feature-gating';
import { PageCommentsCountProvider, NewCommentsProvider } from '@confluence/comment-context';
import {
	OnboardingNthUserContentTypeSpotlight,
	ContentType,
} from '@confluence/experiment-onboarding-content-type-spotlight/entry-points/OnboardingNthUserContentTypeSpotlight';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';

import { useEditorDataLoader } from './useEditorDataLoader';
import { triggerViewedAnalytics } from './LivePageViewedAnalytics';
import { usePermissionCheck } from './usePermissionCheck';
import { LivePagesChangeboardingManager } from './LivePagesChangeboardingManager';
import { LivePageSessionExperienceStart } from './LivePageSessionExperienceStart';
import { LivePageShortcuts } from './LivePageShortcuts';
import { StartLivePageLoad } from './perf.config';

export type LivePageComponentProps = {
	contentId: string;
	spaceKey: string | undefined;
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	customPerfData?: Record<string, string | boolean | object>;
	loadingPriority?: LoadingPriority | null;
};
type LivePageContentProps = {
	canEdit: boolean;
	refetch: () => void;
	onNativeCollabLoad?: (contentId: string, viewMode: boolean | undefined) => void;
	onDraftSyncSuccess?: () => void; // Used to hook into the editor draft sync mutation
} & LivePageComponentProps;

const spinnerContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	height: '100vh',
});

// Handle aborting experiences
/* Why we're aborting each experience:
 * VIEW_PAGE_EXPERIENCE: Aborted due to not wanting live pages to affect core experience
 * VIEW_PAGE_SESSION_EXPERIENCE: Aborted due to not wanting live pages to affect classic view-page-specific experience. Live pages have their own live-page-session experience.
 * CREATE_PAGE_EXPERIENCE: Aborted due to not wanting live pages to affect core experience
 * VIEW_PAGE_EDIT_BUTTON_EXPERIENCE: Aborted due to editable live page starting but never finishing view-page/edit, because in ViewPageCommon, stickyHeader briefly mounts while loading `globalLiveState.isLive` and `editPermission`, which we unmount after we determine doc is an editable live page
 * VIEW_CONTENT_EXPERIENCE: Aborted due to live pages not integrating PerformanceMetrics.tsx, which is used to resolve this experience in normal pages.
 * Core experiences are not aborted if `live_page_exp_to_core_exp_migration` feature gate is enabled.
 */
const useAbortNonLiveExperiences = (isLivePageExpToCoreExpMigrationEnabled: boolean) => {
	const experienceTracker = React.useContext(ExperienceTrackerContext);
	const { hasStopped: hasViewPageExperienceStopped } =
		useExperienceState(VIEW_PAGE_EXPERIENCE) || {};
	const { hasStopped: hasViewPageSessionExperienceStopped } =
		useExperienceState(VIEW_PAGE_SESSION_EXPERIENCE) || {};
	const { hasStopped: hasCreatePageExperienceStopped } =
		useExperienceState(CREATE_PAGE_EXPERIENCE) || {};
	const { hasStopped: hasViewPageEditButtonExperienceStopped } =
		useExperienceState(VIEW_PAGE_EDIT_BUTTON_EXPERIENCE) || {};
	const { hasStopped: hasViewContentExperienceStopped } =
		useExperienceState(VIEW_CONTENT_EXPERIENCE) || {};

	const coreExperiencesToAbort = [
		{ name: VIEW_PAGE_EXPERIENCE, hasStopped: hasViewPageExperienceStopped },
		{
			name: VIEW_CONTENT_EXPERIENCE,
			hasStopped: hasViewContentExperienceStopped,
		},
		{
			name: CREATE_PAGE_EXPERIENCE,
			hasStopped: hasCreatePageExperienceStopped,
		},
	];
	const nonCoreExperiencesToAbort = [
		{ name: VIEW_PAGE_SESSION_EXPERIENCE, hasStopped: hasViewPageSessionExperienceStopped },
		{
			name: VIEW_PAGE_EDIT_BUTTON_EXPERIENCE,
			hasStopped: hasViewPageEditButtonExperienceStopped,
		},
	];

	const experiencesToAbort = isLivePageExpToCoreExpMigrationEnabled
		? nonCoreExperiencesToAbort
		: coreExperiencesToAbort.concat(nonCoreExperiencesToAbort);
	experiencesToAbort.forEach((abortedExperience) => {
		if (!abortedExperience.hasStopped) {
			experienceTracker.abort({
				name: abortedExperience.name,
				reason: 'Live Page Editor was opened',
			});
		}
	});
};

export const LivePageContent = ({
	contentId,
	spaceKey,
	canEdit,
	refetch,
	ClassicEditorContextProviderLoader,
	onNativeCollabLoad,
	customPerfData,
	onDraftSyncSuccess,
	loadingPriority,
}: LivePageContentProps) => {
	const { livePageReleasePhase } = useIsLivePagesFeatureEnabled();

	useAbortNonLiveExperiences(fg('live_page_exp_to_core_exp_migration'));

	const LivePageWrapper = ClassicEditorContextProviderLoader ?? React.Fragment;
	return (
		<LivePageWrapper>
			<StartLivePageLoad contentId={contentId} viewMode={!canEdit} />
			<ContentViewedEvent contentId={contentId} />
			<LivePagesChangeboardingManager origin="live-page" />
			{!fg('live_page_exp_to_core_exp_migration') ? (
				<ExperienceStart name={LIVE_PAGE_VIEW_EXPERIENCE} />
			) : null}
			{fg('live_page_exp_to_core_exp_migration') ? (
				<ExperienceSuccess
					name={CREATE_PAGE_EXPERIENCE}
					attributes={{ isLivePage: true, livePageReleasePhase }}
				/>
			) : null}
			<LivePageSessionExperienceStart />
			<NativeCollabLoader
				contentId={contentId}
				spaceKey={spaceKey}
				contentType="page"
				key={contentId}
				onPermissionChange={refetch}
				canEdit={canEdit}
			>
				{(propsFromNativeCollab: NativeCollabInjectedProps) => {
					const { viewMode } = propsFromNativeCollab;
					const livePageCustomPerfData = {
						...customPerfData,
						viewMode,
					};
					// TODO MODES-3871 Remove this triggerViewedAnalytics side effect when we cleanup LivePageComponent.
					// Trigger live page view analytics event once when the user views a live page.
					onNativeCollabLoad?.(contentId, viewMode);

					return (
						// This TreeRootForServerPlaceholderWrapper placed here to ensure the proper lookup of the placeholders for
						// SSRed loadables. Due to different component trees between server (LivePageComponent) and client (LivePageComponentServer)
						// the placeholder ids hierarchy is different. This wrapper terminates the lookup for placeholder ids on the common parent,
						// so it matches on both server and client.
						<TreeRootForServerPlaceholderWrapper>
							<FullPageEditorLoader
								{...propsFromNativeCollab}
								extensionContextProvider={{
									ClassicEditorContextProvider: ClassicEditorContextProviderLoader,
								}}
								hasEditPermission={canEdit}
								customPerfData={livePageCustomPerfData}
								onDraftSyncSuccess={onDraftSyncSuccess}
								loadingPriority={loadingPriority}
							/>
							<LivePageShortcuts canEdit={canEdit} />
						</TreeRootForServerPlaceholderWrapper>
					);
				}}
			</NativeCollabLoader>
		</LivePageWrapper>
	);
};

export const LivePageComponent = ({
	contentId,
	spaceKey,
	ClassicEditorContextProviderLoader,
	customPerfData,
	loadingPriority,
}: LivePageComponentProps) => {
	const { canEdit, loading, contentQueryError, refetch, networkStatus } =
		usePermissionCheck(contentId);
	const prevCanEditRef = useRef<boolean | undefined>(undefined);
	const hasSentViewedAnalytics = useRef<boolean>(false);
	const pageRef = useRef<{ isFirstLivePage?: boolean; contentId?: string }>({
		isFirstLivePage: false,
		contentId: undefined,
	});

	const { setMode } = useLivePageStoreActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isPreloading } = useEditorDataLoader({
		contentId,
		spaceKey,
	});
	const featureCodes = useFeatureCodesInURL();

	// LivePage Nudges
	const {
		isEligibleForLivePageNudge,
		showSpotlight,
		markFirstLivePageAsSeen,
		livePageNudgeHasSeenFirstLivePage,
		showLivePageInviteFlag,
	} = useLivePageOnboardingNudgeEligible({
		contentId,
	});
	const { isEligible: isLivePageInviteNudgeV2Eligible } = useLivePageInviteNudgeV2Eligible();

	useEffect(() => {
		hasSentViewedAnalytics.current = false;

		return () => {
			// Reset the mode when contentId changes or component unmounts
			void setMode(undefined);
			prevCanEditRef.current = undefined;
		};
	}, [contentId, setMode]);

	useEffect(() => {
		// Permissions on a page can change at anytime. Should update the mode if the new permissions are different from the previous ones.
		const hasPermissionsChanged = canEdit !== prevCanEditRef.current;
		if (hasPermissionsChanged) {
			void setMode(canEdit ? 'edit' : 'view');
		}
		prevCanEditRef.current = canEdit;
	}, [canEdit, contentId, setMode]);

	useEffect(() => {
		if (isEligibleForLivePageNudge) {
			if (!livePageNudgeHasSeenFirstLivePage) {
				markFirstLivePageAsSeen();
				pageRef.current.isFirstLivePage = true;
				pageRef.current.contentId = contentId;
			}

			manuallyLogExpExposure('fix_live_pages_invite_nudge');

			if (!isLivePageInviteNudgeV2Eligible) {
				if (
					!loading &&
					// when live page has been seen before and it's not the current page
					livePageNudgeHasSeenFirstLivePage &&
					(!pageRef.current.isFirstLivePage || contentId !== pageRef.current.contentId)
				) {
					showSpotlight();
				}
			}
		}

		// handles edge case where we visit another page where user didn't contribute
		if (contentId !== pageRef.current.contentId) {
			pageRef.current.isFirstLivePage = false;
		}
	}, [
		loading,
		isEligibleForLivePageNudge,
		markFirstLivePageAsSeen,
		showSpotlight,
		livePageNudgeHasSeenFirstLivePage,
		contentId,
		isLivePageInviteNudgeV2Eligible,
	]);

	const { overrideUFORouteName } = useRouteActions();
	useEffect(() => {
		overrideUFORouteName('live-edit');
		// Live doc transitions trigger a UFO route change but usually don't trigger a LivePageComponent remount, so we need to re-run on contentId change
	}, [overrideUFORouteName, contentId]);

	const handleNativeCollabLoad = (contentId: string, viewMode: boolean | undefined) => {
		if (hasSentViewedAnalytics.current === false) {
			void triggerViewedAnalytics({
				contentId,
				editPermission: canEdit,
				createAnalyticsEvent,
				viewMode,
				...(featureCodes && featureCodes.from ? { from: featureCodes.from } : {}),
			});
			hasSentViewedAnalytics.current = true;
		}
	};

	if (contentQueryError) {
		return <ErrorDisplay error={contentQueryError} />;
	}

	const skipLoadingScreenWhenSSRed = window.__SSR_RENDERED__;
	if (
		(isPreloading && !skipLoadingScreenWhenSSRed) ||
		// don't unmount live pages editor on permission refetch
		(loading && networkStatus !== NetworkStatus.refetch)
	) {
		return (
			<Box xcss={spinnerContainerStyles}>
				<Spinner interactionName="live-pages-loading-spinner" size="medium" />
			</Box>
		);
	}

	return (
		<NewCommentsProvider>
			<PageCommentsCountProvider contentId={contentId}>
				<LivePageContent
					contentId={contentId}
					spaceKey={spaceKey}
					ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
					canEdit={canEdit}
					refetch={refetch}
					onNativeCollabLoad={handleNativeCollabLoad}
					customPerfData={customPerfData}
					onDraftSyncSuccess={showLivePageInviteFlag}
					loadingPriority={loadingPriority}
				/>
				<OnboardingNthUserContentTypeSpotlight
					contentType={ContentType.LIVE_DOC}
					contentId={contentId}
				/>
				<LivePageInviteNudge />
			</PageCommentsCountProvider>
		</NewCommentsProvider>
	);
};
